import React, { useState, useEffect } from 'react';
import { FiSearch, FiEdit3 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const InventoryDashboard = () => {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const rowsPerPage = 15;
  const navigate = useNavigate();

  // Fetch data from the API
  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BQ_BASE_URL}/bq/api/order_summaries`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Ensure data is an array before setting state
      if (Array.isArray(data)) {
        setAllData(data);
        setFilteredData(data);
      } else {
        console.error('API response is not an array:', data);
        toast.error('Received invalid data format from server');
        setAllData([]);
        setFilteredData([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
      setAllData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle Search input changes
  const handleSearch = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchTerm(searchValue);
    filterData(searchValue, selectedStatus);
  };

  // Handle Status change in the dropdown
  const handleStatusChange = (e) => {
    const status = e.target.value;
    setSelectedStatus(status);
    filterData(searchTerm, status);
  };

  // Filter the data based on search term and status
  const filterData = (searchValue, status) => {
    if (!Array.isArray(allData)) {
      setFilteredData([]);
      return;
    }

    const filtered = allData.filter((item) => {
      const matchesSearch =
        (item.bookingid?.toLowerCase() || '').includes(searchValue) ||
        (item.guestid?.toLowerCase() || '').includes(searchValue);

      const matchesStatus = status
        ? (item.gueststatus?.toLowerCase() || '') === status.toLowerCase()
        : true;

      return matchesSearch && matchesStatus;
    });
    setFilteredData(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Get the status indicator color
  const getStatusIndicator = (status) => {
    if (!status) return 'bg-gray-500';

    const normalizedStatus = status.toLowerCase();
    const statusColors = {
      active: 'bg-green-500',
      inactive: 'bg-red-500',
    };
    return statusColors[normalizedStatus] || 'bg-gray-500';
  };

  // Calculate pagination values
  const safeFilteredData = Array.isArray(filteredData) ? filteredData : [];
  const totalPages = Math.ceil(safeFilteredData.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = safeFilteredData.slice(indexOfFirstRow, indexOfLastRow);

  // Pagination handlers
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  // Logic for showing page buttons
  const getPageButtons = () => {
    const maxPagesToShow = 10;
    let startPage = Math.max(currentPage - Math.floor(maxPagesToShow / 2), 1);
    let endPage = Math.min(startPage + maxPagesToShow - 1, totalPages);

    // Adjust if we're near the end
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(endPage - maxPagesToShow + 1, 1);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  // Handle Refresh button
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/create_order_summaries`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: '',
      });

      if (response.ok) {
        toast.success('Order summaries refreshed successfully');
        await fetchData(); // Refetch the data after refresh
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Failed to refresh order summaries');
    } finally {
      setRefreshing(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString; // Return raw string if date parsing fails
    }
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen flex flex-col">
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Room Inventory</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/room-management/overview')}
            className="btn"
          >
            Overview
          </button>
          <button onClick={() => navigate('/dashboard/mdm/room-management/rooms')} className="btn">
            Room Management
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/room-management/order-summary')}
            className="btn text-black font-bold"
          >
            Order Summary
          </button>
        </div>
      </header>

      <div className="text-center mb-2">
        <div className="inline-block">
          <h1 className="text-2xl font-bold text-center text-black">Billing & Order Summary</h1>
          <h1 className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
            See detailed order charges, applicable taxes, and how much is left to pay in the table.
          </h1>
          <hr className="border-gray-400 mt-1 w-96 mx-auto" />
        </div>
      </div>

      <div className="flex-1">
        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-2">Loading data...</p>
          </div>
        )}

        {!loading && (
          <>
            <div className="mb-4 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 md:space-x-4">
              <div className="relative w-full md:w-1/3">
                <input
                  type="text"
                  placeholder="Search by Booking ID or Guest ID"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="p-3 pl-12 h-12 border w-full border-gray-300 rounded-lg shadow-md"
                />
                <FiSearch className="absolute top-3 left-4 text-gray-500" size={20} />
              </div>

              <div className="flex flex-col md:flex-row gap-4 items-center w-full md:w-auto">
                <button
                  onClick={handleRefresh}
                  className="px-6 py-3 bg-cyan-500 text-white rounded-lg shadow-lg hover:text-black flex items-center gap-2 h-12"
                  disabled={refreshing}
                >
                  {refreshing ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      <span className="text-sm">Refreshing...</span>
                    </>
                  ) : (
                    <>
                      <FiEdit3 size={18} />
                      <span className="text-sm">Refresh</span>
                    </>
                  )}
                </button>
                <select
                  onChange={handleStatusChange}
                  value={selectedStatus}
                  className="px-4 py-2 bg-white text-black rounded-lg shadow-md h-12 border border-gray-300"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {safeFilteredData.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg shadow-md">
                <p className="text-gray-500">
                  {searchTerm || selectedStatus ? 'No matching records found' : 'No data available'}
                </p>
              </div>
            ) : (
              <>
                <div className="shadow-md rounded-lg overflow-x-auto">
                  <table className="table-auto w-full text-sm bg-white border border-gray-200 rounded-lg">
                    <thead>
                      <tr className="bg-blue-50 text-gray-600 uppercase text-xs">
                        <th className="p-3 text-left">Guest Status</th>
                        <th className="p-3 text-left">Booking ID</th>
                        <th className="p-3 text-left">Client Type</th>
                        <th className="p-3 text-left">Check-In Date</th>
                        <th className="p-3 text-left">Check-Out Date</th>
                        <th className="p-3 text-left">Total Amount</th>
                        <th className="p-3 text-left">Taxable Amount</th>
                        <th className="p-3 text-left">Pending Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRows.map((item, index) => (
                        <tr
                          key={`${item.summaryid || index}`}
                          className="hover:bg-gray-50 border-t"
                        >
                          <td className="p-3 flex items-center">
                            <span
                              className={`inline-block w-4 h-4 rounded-full ${getStatusIndicator(
                                item.gueststatus
                              )}`}
                            ></span>
                            <span className="ml-2">{item.gueststatus || 'N/A'}</span>
                          </td>
                          <td className="p-3">{item.bookingid || 'N/A'}</td>
                          <td className="p-3">{item.clienttype || 'N/A'}</td>
                          <td className="p-3">{formatDate(item.checkindate)}</td>
                          <td className="p-3">{formatDate(item.checkoutdate)}</td>
                          <td className="p-3">₹{item.totalamount}</td>
                          <td className="p-3">₹{item.taxableamount}</td>
                          <td className="p-3">₹{item.pendingamount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="flex justify-center mt-6 text-xs gap-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className="px-3 py-1 bg-black text-white rounded disabled:opacity-50"
                    >
                      Previous
                    </button>

                    {getPageButtons().map((page) => (
                      <button
                        key={page}
                        onClick={() => paginate(page)}
                        className={`px-3 py-1 rounded-full ${
                          currentPage === page
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 hover:bg-gray-300'
                        }`}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 bg-black text-white rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default InventoryDashboard;

// import React, { useState, useEffect } from "react";
// import { FiSearch, FiEdit3 } from "react-icons/fi";
// import { useNavigate } from "react-router-dom";
// import { ToastContainer, toast } from "react-toastify"; // Import ToastContainer and toast
// import "react-toastify/dist/ReactToastify.css"; // Import the Toast CSS
// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const InventoryDashboard = () => {
//   const [allData, setAllData] = useState([]);
//   const [filteredData, setFilteredData] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [selectedStatus, setSelectedStatus] = useState("");
//   const [currentPage, setCurrentPage] = useState(1);
//   const [loading, setLoading] = useState(false); // State to manage loading
//   const rowsPerPage = 15; // Adjusted to show more rows
//   const navigate = useNavigate();

//   // Fetch data from the API
//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const response = await fetch(`${BQ_BASE_URL}/bq/api/order_summaries`);
//         const data = await response.json();
//         setAllData(data);
//         setFilteredData(data);
//       } catch (error) {
//         console.error("Error fetching data:", error);
//       }
//     };

//     fetchData();
//   }, []);

//   // Handle Search input changes
//   const handleSearch = (e) => {
//     const searchValue = e.target.value.toLowerCase();
//     setSearchTerm(searchValue);
//     filterData(searchValue, selectedStatus);
//   };

//   // Handle Status change in the dropdown
//   const handleStatusChange = (e) => {
//     const status = e.target.value;
//     setSelectedStatus(status);
//     filterData(searchTerm, status);
//   };

//   // Filter the data based on search term and status
//   const filterData = (searchValue, status) => {
//     const filtered = allData.filter((item) => {
//       const matchesSearch =
//         item.bookingid.toLowerCase().includes(searchValue) ||
//         item.guestid.toLowerCase().includes(searchValue);

//       const matchesStatus = status
//         ? item.gueststatus.toLowerCase() === status.toLowerCase()
//         : true;

//       return matchesSearch && matchesStatus;
//     });
//     setFilteredData(filtered);
//   };

//   // Get the status indicator color
//   const getStatusIndicator = (status) => {
//     const normalizedStatus = status.toLowerCase();
//     const statusColors = {
//       active: "bg-green-500",
//       inactive: "bg-red-500",
//     };
//     return statusColors[normalizedStatus] || "bg-gray-500";
//   };

//   const totalPages = Math.ceil(filteredData.length / rowsPerPage);
//   const indexOfLastRow = currentPage * rowsPerPage;
//   const indexOfFirstRow = indexOfLastRow - rowsPerPage;
//   const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

//   // Pagination logic to show 5 pages at a time
//   const paginate = (pageNumber) => setCurrentPage(pageNumber);

//   const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
//   const handleNextPage = () =>
//     setCurrentPage((prev) => Math.min(prev + 1, totalPages));

//   // Logic for showing page buttons
//   const getPageButtons = () => {
//     let pageButtons = [];

//     // Calculate the range of pages to show
//     const maxPagesToShow = 10;
//     let startPage = Math.max(currentPage - Math.floor(maxPagesToShow / 2), 1);
//     let endPage = Math.min(startPage + maxPagesToShow - 1, totalPages);

//     // Adjust startPage if we are near the end of the pages
//     if (endPage - startPage < maxPagesToShow - 1) {
//       startPage = Math.max(endPage - maxPagesToShow + 1, 1);
//     }

//     // Create the page buttons for the range
//     for (let i = startPage; i <= endPage; i++) {
//       pageButtons.push(i);
//     }

//     return pageButtons;
//   };

//   // Function to handle Refresh button
//   const handleRefresh = async () => {
//     setLoading(true); // Start loader

//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/create_order_summaries`,
//         {
//           method: "POST",
//           headers: { Accept: "application/json" },
//           body: "",
//         }
//       );

//       if (response.ok) {
//         const result = await response.json();
//         toast.success("Order summaries processed successfully"); // Show success toast
//         setAllData([]); // Optionally reset data if needed
//         setFilteredData([]);
//         window.location.reload();
//       } else {
//         toast.error("Failed to process order summaries");
//       }
//     } catch (error) {
//       toast.error("Error occurred while processing");
//       console.error("Error processing order summaries:", error);
//     }

//     setLoading(false); // Stop loader
//   };

//   return (
//     <div className="p-6 bg-gray-100 min-h-screen flex flex-col">
//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">Room Inventory</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() => navigate("/dashboard/mdm/room-management/overview")}
//             className="btn"
//           >
//             Overview
//           </button>
//           <button
//             onClick={() => navigate("/dashboard/mdm/room-management/rooms")}
//             className="btn"
//           >
//             Room Management
//           </button>
//           <button
//             onClick={() =>
//               navigate("/dashboard/mdm/room-management/order-summary")
//             }
//             className="btn text-black font-bold"
//           >
//             Order Summary
//           </button>
//         </div>
//       </header>
//       <div className="text-center mb-2">
//         <div className="inline-block">
//           <h1 className="text-2xl font-bold text-center text-black">
//             Billing & Order Summary
//           </h1>
//           <h1 className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
//             See detailed order charges, applicable taxes, and how much is left
//             to pay in the table.
//           </h1>
//           <hr className="border-gray-400 mt-1 w-96 mx-auto" />
//         </div>
//       </div>
//       <div className="flex-1">
//         <div className="mb-4 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 md:space-x-4">
//           <div className="relative w-full md:w-1/3">
//             <input
//               type="text"
//               placeholder="Search by Booking ID"
//               value={searchTerm}
//               onChange={handleSearch}
//               className="p-3 pl-12 h-12 border w-full border-gray-300 rounded-lg shadow-md"
//             />
//             <FiSearch
//               className="absolute top-3 left-4 text-gray-500"
//               size={20}
//             />
//           </div>

//           <div className="flex flex-col md:flex-row gap-4 items-center w-full md:w-auto">
//             <button
//               onClick={handleRefresh} // On refresh button click, call handleRefresh
//               className="px-6 py-3 bg-cyan-500 text-white rounded-lg shadow-lg hover:text-black flex items-center gap-2 h-12"
//               disabled={loading} // Disable button while loading
//             >
//               {loading ? (
//                 <span>Refreshing...</span> // Show loading text or spinner
//               ) : (
//                 <>
//                   <FiEdit3 size={18} />
//                   <span className="text-sm">Refresh</span>
//                 </>
//               )}
//             </button>
//             <select
//               onChange={handleStatusChange}
//               value={selectedStatus}
//               className="px-4 py-2 bg-white text-black rounded-lg shadow-md h-12 border border-gray-300"
//             >
//               <option value="">All Status</option>
//               <option value="active">Active</option>
//               <option value="inactive">Inactive</option>
//             </select>
//           </div>
//         </div>

//         <div className="shadow-md rounded-lg overflow-x-auto">
//           <table className="table-auto w-full text-sm bg-white border border-gray-200 rounded-lg">
//             <thead>
//               <tr className="bg-blue-50 text-gray-600 uppercase text-xs">
//                 <th className="p-3 text-left">Guest Status</th>
//                 <th className="p-3 text-left">Booking ID</th>
//                 <th className="p-3 text-left">Client Type</th>
//                 <th className="p-3 text-left">Check-In Date</th>

//                 <th className="p-3 text-left">Check-Out Date</th>

//                 <th className="p-3 text-left">Total Amount</th>
//                 <th className="p-3 text-left">Taxable Amount</th>
//               </tr>
//             </thead>
//             <tbody>
//               {currentRows.map((item) => (
//                 <tr key={item.summaryid} className="hover:bg-gray-50 border-t">
//                   <td className="p-3 flex items-center">
//                     <span
//                       className={`inline-block w-4 h-4 rounded-full ${getStatusIndicator(
//                         item.gueststatus
//                       )}`}
//                     ></span>
//                     <span className="ml-2">{item.gueststatus}</span>
//                   </td>
//                   <td className="p-3">{item.bookingid}</td>
//                   <td className="p-3">{item.clienttype}</td>
//                   <td className="p-3">{item.checkindate || "N/A"}</td>

//                   <td className="p-3">{item.checkoutdate || "N/A"}</td>

//                   <td className="p-3">{item.totalamount}</td>
//                   <td className="p-3">{item.taxableamount}</td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>

//         <div className="flex justify-center mt-6 text-xs gap-2">
//           <button
//             onClick={handlePrevPage}
//             disabled={currentPage === 1}
//             className="px-3 py-1 bg-black text-white rounded"
//           >
//             Previous
//           </button>

//           {getPageButtons().map((page) => (
//             <button
//               key={page}
//               onClick={() => paginate(page)}
//               className={`px-3 py-1 rounded-full ${
//                 currentPage === page ? "bg-blue-500 text-white" : "bg-gray-200"
//               }`}
//             >
//               {page}
//             </button>
//           ))}

//           <button
//             onClick={handleNextPage}
//             disabled={currentPage === totalPages}
//             className="px-4 py-2 bg-black text-white rounded"
//           >
//             Next
//           </button>
//         </div>
//       </div>
//       <ToastContainer /> {/* Add ToastContainer to display notifications */}
//     </div>
//   );
// };

// export default InventoryDashboard;
